import { useEffect, useState } from 'react';
import { useEmergencyStore } from '@/stores/emergency-store';
import { EmergencyMap } from '@/components/emergency/EmergencyMap';
import { AlertQueue } from '@/components/emergency/AlertQueue';
import { AlertDetail } from '@/components/emergency/AlertDetail';
import { PatrolList } from '@/components/emergency/PatrolList';
import { ClientList } from '@/components/emergency/ClientList';
import { SiteList } from '@/components/emergency/SiteList';
import { ArcEventDetail } from '@/components/emergency/ArcEventDetail';
import { SiteDetail } from '@/components/emergency/SiteDetail';
import { AlertToastContainer } from '@/components/emergency/AlertToastContainer';
import { ensureNotificationPermission } from '@/services/status-notifier';
import { Bell, Users, Smartphone, Building2 } from 'lucide-react';

export function EmergencyPage() {
  const {
    initializeLocation,
    fetchActiveAlerts,
    fetchPatrols,
    fetchClients,
    fetchSitesAndEvents,
    subscribeToRealtime,
    unsubscribe,
    activeAlerts,
    selectedAlert,
    patrols,
    clients,
    sites,
    arcEvents,
    selectedArcEvent,
    selectedSite,
  } = useEmergencyStore();

  const [rightPanel, setRightPanel] = useState<'alerts' | 'patrols' | 'clients' | 'sites'>('alerts');

  // Auto-switch to alerts tab whenever an alert is selected (e.g. click on map marker)
  useEffect(() => {
    if (selectedAlert) setRightPanel('alerts');
  }, [selectedAlert]);

  useEffect(() => {
    let cancelled = false;

    // The store is a module-level singleton — it persists across tab switches.
    // Only run the full init sequence (fetch + subscribe) once per session.
    // If `unsubscribe` is already set the Realtime subscription is live and
    // data is current; we just need the polling interval.
    initializeLocation();

    if (!unsubscribe) {
      // First mount this session: fetch data then open Realtime.
      (async () => {
        await fetchActiveAlerts();
        if (cancelled) return;
        await fetchPatrols();
        if (cancelled) return;
        await fetchClients();
        if (cancelled) return;
        await fetchSitesAndEvents();
        if (cancelled) return;
        subscribeToRealtime();
      })();
    } else {
      // Returning to this tab: silently refresh so data is up-to-date.
      fetchActiveAlerts(true);
      fetchPatrols(true);
      fetchClients(true);
      fetchSitesAndEvents(true);
    }

    // Realtime is the source of truth. This is a safety-net fallback in case
    // a Realtime event is dropped (we've seen client status get "stuck" on the
    // dashboard while the device had already reported BACKGROUND). 15s keeps
    // the dashboard responsive without hammering Supabase. Serialized to
    // avoid auth-lock contention.
    const interval = setInterval(async () => {
      await fetchActiveAlerts(true);
      await fetchPatrols(true);
      await fetchClients(true);
      fetchSitesAndEvents(true);
    }, 15000);

    // Ask the OS for permission to raise desktop notifications. Browsers
    // block this on page load — instead we attach it to the first user click
    // anywhere on the dashboard. Once granted/denied the listener removes
    // itself so we don't keep firing the prompt logic on every click.
    const askOnce = () => {
      void ensureNotificationPermission();
      window.removeEventListener('click', askOnce);
    };
    window.addEventListener('click', askOnce, { once: true });

    // On unmount only stop the interval — keep the store and Realtime alive
    // so navigating back to this tab is instant with no re-fetch flicker.
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('click', askOnce);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="h-full flex">
      {/* Main Map Area */}
      <div className="flex-1 relative">
        <EmergencyMap className="w-full h-full" />

        {/* Stats overlay */}
        <div className="absolute top-4 left-4 flex gap-3">
          <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-gray-200 dark:border-transparent">
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">Centro de Monitoreo</h1>
            <p className="text-gray-500 dark:text-slate-400 text-sm">Alertas y patrulleros en tiempo real</p>
          </div>

          <div className="bg-red-600/90 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg flex items-center gap-3">
            <Bell className="w-5 h-5 text-white" />
            <div>
              <p className="text-white font-bold text-xl">{activeAlerts.filter(a => a.status === 'PENDING').length}</p>
              <p className="text-red-200 text-xs">Pendientes</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Alerts or Patrols */}
      <div className="w-[400px] bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-slate-800 flex flex-col">
        {/* Panel tabs — compact column layout (icon + label + count badge)
            so all four tabs fit comfortably in the 400px panel without
            clipping the rightmost label. */}
        <div className="flex border-b border-gray-200 dark:border-slate-800">
          <button
            onClick={() => setRightPanel('alerts')}
            title={`Alertas (${activeAlerts.length})`}
            className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 px-1 py-2 text-[11px] font-medium transition-colors ${
              rightPanel === 'alerts'
                ? 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white border-b-2 border-red-500'
                : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-800/50'
            }`}
          >
            <div className="flex items-center gap-1">
              <Bell className="w-4 h-4" />
              <span className="text-[10px] font-semibold tabular-nums px-1 rounded bg-gray-200/70 dark:bg-slate-700/70">
                {activeAlerts.length}
              </span>
            </div>
            <span className="truncate w-full text-center">Alertas</span>
          </button>
          <button
            onClick={() => setRightPanel('patrols')}
            title={`Patrulleros (${patrols.filter((p) => p.status !== 'OFFLINE').length}/${patrols.length})`}
            className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 px-1 py-2 text-[11px] font-medium transition-colors ${
              rightPanel === 'patrols'
                ? 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white border-b-2 border-green-500'
                : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-800/50'
            }`}
          >
            <div className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              <span className="text-[10px] font-semibold tabular-nums px-1 rounded bg-gray-200/70 dark:bg-slate-700/70">
                {patrols.filter((p) => p.status !== 'OFFLINE').length}/{patrols.length}
              </span>
            </div>
            <span className="truncate w-full text-center">Patrullas</span>
          </button>
          <button
            onClick={() => setRightPanel('clients')}
            title={`Clientes (${clients.length})`}
            className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 px-1 py-2 text-[11px] font-medium transition-colors ${
              rightPanel === 'clients'
                ? 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white border-b-2 border-blue-500'
                : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-800/50'
            }`}
          >
            <div className="flex items-center gap-1">
              <Smartphone className="w-4 h-4" />
              <span className="text-[10px] font-semibold tabular-nums px-1 rounded bg-gray-200/70 dark:bg-slate-700/70">
                {clients.length}
              </span>
            </div>
            <span className="truncate w-full text-center">Clientes</span>
          </button>
          <button
            onClick={() => setRightPanel('sites')}
            title={`Cuentas (${sites.length}${arcEvents.length > 0 ? ` · ${arcEvents.length} eventos` : ''})`}
            className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 px-1 py-2 text-[11px] font-medium transition-colors ${
              rightPanel === 'sites'
                ? 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white border-b-2 border-amber-500'
                : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-800/50'
            }`}
          >
            <div className="flex items-center gap-1">
              <Building2 className="w-4 h-4" />
              <span className="text-[10px] font-semibold tabular-nums px-1 rounded bg-gray-200/70 dark:bg-slate-700/70">
                {arcEvents.length > 0 ? `${arcEvents.length}·` : ''}{sites.length}
              </span>
            </div>
            <span className="truncate w-full text-center">Cuentas</span>
          </button>
        </div>

        {/* Panel content */}
        <div className="flex-1 overflow-hidden">
          {selectedArcEvent ? (
            <ArcEventDetail />
          ) : selectedSite ? (
            // Rich account/site card — surfaced whenever the operator clicks
            // a site marker on the map or a row in the right-panel list,
            // unless an ARC event detail is already open (event takes priority).
            <SiteDetail />
          ) : rightPanel === 'alerts' ? (
            selectedAlert ? (
              <AlertDetail />
            ) : (
              <AlertQueue />
            )
          ) : rightPanel === 'patrols' ? (
            <PatrolList />
          ) : rightPanel === 'clients' ? (
            <ClientList />
          ) : (
            <SiteList />
          )}
        </div>
      </div>

      {/* In-app toast notifications for new/assigned alerts */}
      <AlertToastContainer />
    </div>
  );
}
